import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SocieteModal, { countries } from "../components/SocieteModal";
import InseeCompanySearch from "../components/InseeCompanySearch";
import { db, auth } from "../firebase";
import { doc, updateDoc, deleteField, onSnapshot, setDoc, getDoc } from "firebase/firestore";
import "../styles/Modal.css";
import "../styles/societes.css";
import { Company } from "../types";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { logUserAction } from "../utils/logUtils";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from '../hooks/usePageTitle';
import * as XLSX from 'xlsx';

const SocietesFoodinnov: React.FC = () => {
  usePageTitle("Societes Foodinnov");
  const [companiesObj, setCompaniesObj] = useState<{ [key: string]: Company }>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState<Company | null>(null);
  const [companyToDelete, setCompanyToDelete] = useState<{ key: string; name: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState(() => localStorage.getItem("sortOption") || "created");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    () => (localStorage.getItem("sortDirection") as "asc" | "desc") || "desc"
  );
  const [companiesPerPage, setCompaniesPerPage] = useState<number>(
    () => Number(localStorage.getItem("companiesPerPage")) || 15
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedContact, setSelectedContact] = useState<{
    name: string;
    phone?: string;
    email?: string;
    fonction?: string;
    gender?: string;
    type: "principal" | "autre";
    principalContact?: string;
    principalFirstName?: string;
    nom?: string;
    prenom?: string;
  } | null>(null);
  const [isInseeModalOpen, setIsInseeModalOpen] = useState(false);
  const [selectedFromInsee, setSelectedFromInsee] = useState<Partial<Company> | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  useEffect(() => {
    const ref = doc(db, "prospects", "foodinnovCompanies");
    return onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as { [k: string]: Company };
        // Nettoyer les enregistrements vides
        const cleanedData: { [k: string]: Company } = {};
        Object.entries(data).forEach(([key, company]) => {
          if (company && company.name && company.name.trim() !== "") {
            cleanedData[key] = company;
          }
        });
        setCompaniesObj(cleanedData);
      } else {
        setCompaniesObj({});
      }
    });
  }, []);

  useEffect(() => {
    localStorage.setItem("sortOption", sortOption);
    localStorage.setItem("sortDirection", sortDirection);
    localStorage.setItem("companiesPerPage", companiesPerPage.toString());
  }, [sortOption, sortDirection, companiesPerPage]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCompanyToEdit(null);
    setSelectedFromInsee(null);
    setIsInseeModalOpen(false);
  };

  const handleSocieteSubmit = async (data: Company) => {
    const ref = doc(db, "prospects", "foodinnovCompanies");
    const now = new Date();

    if (!data.name || typeof data.name !== "string" || data.name.trim() === "") {
      toast.error("Le nom de la société est requis.");
      return;
    }

    const cleanKey = data.name.replace(/[./]/g, "_");

    try {
      const isEditing = !!companyToEdit;
      const actionType = isEditing ? "Modification société" : "Ajout société";
      const target = data.name;

      console.log("Tentative d'enregistrement de la société :", data.name);

      // Si on modifie le nom, récupérer toutes les données de l'ancienne société
      let existingData: any = {};
      if (isEditing && companyToEdit.name !== data.name) {
        const oldKey = companyToEdit.name.replace(/[./]/g, "_");
        const docSnap = await getDoc(ref);
        if (docSnap.exists()) {
          const allData = docSnap.data();
          const oldCompanyData = allData[oldKey];
          if (oldCompanyData) {
            // Préserver toutes les données existantes (opportunities, invoices, devis, etc.)
            existingData = {
              opportunities: oldCompanyData.opportunities || [],
              invoices: oldCompanyData.invoices || [],
              devis: oldCompanyData.devis || [],
              // Préserver tous les autres champs non modifiés
              ...Object.fromEntries(
                Object.entries(oldCompanyData).filter(([key]) => 
                  !['name', 'siret', 'address', 'postalCode', 'city', 'country',
                    'principalGender', 'principalContact', 'principalFirstName',
                    'principalPhone', 'principalEmail', 'principalPosition',
                    'otherContacts', 'createdAt', 'updatedAt', 'id'].includes(key)
                )
              )
            };
            console.log("Données existantes récupérées pour migration:", Object.keys(existingData));
          }
        }
      } else if (isEditing) {
        // Si on modifie sans changer le nom, récupérer aussi les données existantes
        const docSnap = await getDoc(ref);
        if (docSnap.exists()) {
          const allData = docSnap.data();
          const oldKey = companyToEdit.name.replace(/[./]/g, "_");
          const oldCompanyData = allData[oldKey];
          if (oldCompanyData) {
            existingData = {
              opportunities: oldCompanyData.opportunities || [],
              invoices: oldCompanyData.invoices || [],
              devis: oldCompanyData.devis || [],
              ...Object.fromEntries(
                Object.entries(oldCompanyData).filter(([key]) => 
                  !['name', 'siret', 'address', 'postalCode', 'city', 'country',
                    'principalGender', 'principalContact', 'principalFirstName',
                    'principalPhone', 'principalEmail', 'principalPosition',
                    'otherContacts', 'createdAt', 'updatedAt', 'id'].includes(key)
                )
              )
            };
          }
        }
      }

      const companyData: any = {
        ...data,
        name: data.name.trim(),
        otherContacts: (data.otherContacts || []).map((c) => ({
          civilite: c.civilite || "Mr",
          nom: c.nom || "",
          prenom: c.prenom || "",
          tel: c.tel || "",
          email: c.email || "",
          fonction: c.fonction || "",
        })),
        principalPosition: data.principalPosition || "",
        createdAt: isNaN(new Date(data.createdAt as any).getTime()) ? now : new Date(data.createdAt as any),
        updatedAt: now,
        id: data.id || Date.now(),
        // Inclure les données existantes (opportunities, invoices, devis, etc.)
        ...existingData,
      };

      // Si le nom a changé, créer la nouvelle clé d'abord, puis supprimer l'ancienne
      if (isEditing && companyToEdit.name !== data.name) {
        const oldKey = companyToEdit.name.replace(/[./]/g, "_");
        // Créer la nouvelle clé avec toutes les données
        await setDoc(ref, { [cleanKey]: companyData }, { merge: true });
        console.log("Nouvelle clé créée avec toutes les données.");
        // Ensuite supprimer l'ancienne clé
        await updateDoc(ref, { [oldKey]: deleteField() });
        console.log("Ancienne clé supprimée après migration des données.");
      } else {
        // Si le nom n'a pas changé, juste mettre à jour
        await setDoc(ref, { [cleanKey]: companyData }, { merge: true });
        console.log("Société enregistrée dans Firestore.");
      }

      // Enregistrer l'action utilisateur
      const userEmail = auth.currentUser?.email;
      if (userEmail) {
        console.log("Enregistrement de l'action utilisateur:", actionType, target);
        await logUserAction(
          userEmail,
          actionType,
          target
        );
        console.log("Action utilisateur enregistrée.");
      } else {
        console.warn("Pas d'utilisateur connecté pour enregistrer l'action.");
      }

      toast.success(`Société "${data.name}" enregistrée.`);
    } catch (err: any) {
      console.error("Erreur Firestore lors de l'enregistrement de la société :", err?.message || err);
      toast.error("Erreur lors de l'enregistrement : " + (err?.message || "inconnue"));
    } finally {
        handleCloseModal();
    }
  };

  const handleDeleteCompany = async (companyKey: string) => {
    const company = companiesObj[companyKey];
    if (!company) return;
    setCompanyToDelete({ key: companyKey, name: company.name });
  };

  const confirmDelete = async () => {
    if (!companyToDelete) return;
    try {
      console.log("Tentative de suppression de la société avec la clé:", companyToDelete.key);
      const ref = doc(db, "prospects", "foodinnovCompanies");
      
      // Vérifier si le document existe avant la suppression
      const docSnap = await getDoc(ref);
      if (!docSnap.exists()) {
        throw new Error("Document non trouvé");
      }

      // Vérifier si la société existe dans le document
      const data = docSnap.data();
      if (!data[companyToDelete.key]) {
        throw new Error("Société non trouvée dans le document");
      }

      await updateDoc(ref, { [companyToDelete.key]: deleteField() });
      console.log("Société supprimée avec succès");

      // Enregistrer l'action utilisateur
      const userEmail = auth.currentUser?.email;
      if (userEmail) {
        await logUserAction(
          userEmail,
          "Suppression société",
          companyToDelete.key
        );
        console.log("Action utilisateur enregistrée");
      }

      toast.success("Société supprimée avec succès", { className: 'toast-delete-success' });
    } catch (err: any) {
      console.error("Erreur détaillée lors de la suppression:", err);
      toast.error(`Erreur lors de la suppression: ${err.message}`);
    } finally {
      setCompanyToDelete(null);
    }
  };

  const formatDemandes = (company: Company): string => {
    const regroup = (company.opportunities || []).reduce((acc: Record<string, number>, r: { type: string }) => {
      const lib = r.type;
      let short = "";
      if (lib?.includes("FDA")) short = "FDA (USA)";
      else if (lib?.includes("TTB")) short = "TTB (USA)";
      else if (lib?.includes("CIFER")) short = "CIFER (Chine)";
      else if (lib?.includes("Notices")) short = "Notices (USA)";
      else if (lib?.includes("Conseil réglementaire (heure)")) short = "Conseil (h)";
      else if (lib?.includes("Conseil réglementaire (journée)")) short = "Conseil (j)";
      else if (lib?.includes("Abonnement à la Database")) short = "DB";
      if (!short) return acc;
      acc[short] = (acc[short] || 0) + 1;
      return acc;
    }, {});
    return (Object.entries(regroup) as [string, number][])
      .map(([type]) => type)
      .join(" + ") || "—";
  };

    const companies = Object.entries(companiesObj).map(([k, d]) => ({ companyKey: k, ...d }));
  const sorted = [...companies].sort((a, b) => {
    const getDateValue = (date: any) => {
      if (!date) return 0;
      if (date instanceof Date) return date.getTime();
      if (typeof date === 'string') return new Date(date).getTime();
      if (date.toDate) return date.toDate().getTime();
      return 0;
    };

    const cmp =
      sortOption === "alphabetical"
        ? (a.name || "").localeCompare(b.name || "")
        : sortOption === "created"
        ? getDateValue(a.createdAt) - getDateValue(b.createdAt)
        : getDateValue(a.updatedAt) - getDateValue(b.updatedAt);
    return sortDirection === "asc" ? cmp : -cmp;
  });
  const displayed = sorted.filter((c) => {
    // Exclure les sociétés sans nom ou avec un nom vide
    if (!c.name || c.name.trim() === "") {
      return false;
    }
    return (c.name || "").toLowerCase().includes(searchQuery.toLowerCase());
  });
  const totalPages = Math.ceil(displayed.length / companiesPerPage);
  const startIndex = (currentPage - 1) * companiesPerPage;
  const currentCompanies = displayed.slice(startIndex, startIndex + companiesPerPage);

  return (
    <div className="societes-page flex flex-col min-h-screen">
      <Header 
        isAuthenticated={true} 
        onLoginClick={() => navigate('/login')} 
        onHomeClick={() => navigate('/')} 
        onLogoutClick={handleLogout}
      />
      <main className="societes-container">
        <h1 className="societes-title">Liste des Sociétés Foodinnov :</h1>

        <div className="actions-bar">
          <button className="add-societe-button" onClick={() => {
            setCompanyToEdit(null);
            setSelectedFromInsee(null);
            setIsModalOpen(true);
          }}>+ Ajouter une société</button>

          <button className="add-societe-button" onClick={() => {
            setIsInseeModalOpen(true);
            setIsModalOpen(false);
          }}>Ajouter depuis Siren</button>

          <button className="add-societe-button" onClick={() => {
            const headers = [
              "Raison sociale", "N° Siret", "Adresse principale Adresse 1", 
              "Adresse principale Code postal", "Adresse principale Ville", 
              "Adresse principale Pays", "Téléphone 1", "Email"
            ];
            
            const excelData = [headers];
            displayed.forEach((c) => {
              const row = [
                c.name || "", 
                c.siret || "", 
                c.address || "", 
                c.postalCode || "", 
                c.city || "", 
                countries.find(ct => ct.value === c.country)?.label || c.country || "",
                c.principalPhone || "", 
                c.principalEmail || ""
              ];
              excelData.push(row);
            });

            // Créer un workbook et une worksheet
            const ws = XLSX.utils.aoa_to_sheet(excelData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sociétés Foodinnov");

            // Générer le fichier Excel
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            
            // Télécharger le fichier
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.setAttribute("download", "societes_foodinnov.xlsx");
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}>Exporter en Excel</button>

          <input type="text" className="search-bar" placeholder="Rechercher…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />

          <select value={sortOption} onChange={(e) => setSortOption(e.target.value)} className="sort-dropdown">
            <option value="created">Date de création</option>
            <option value="alphabetical">Nom</option>
            <option value="updated">Date de modification</option>
          </select>

          <button className="direction-button" onClick={() => setSortDirection(d => d === "asc" ? "desc" : "asc")}>
            {sortDirection === "asc" ? "↑" : "↓"}
          </button>

          <select className="nbre-pages" value={companiesPerPage} onChange={(e) => setCompaniesPerPage(Number(e.target.value))}>
            <option value={15}>15 par page</option>
            <option value={30}>30 par page</option>
            <option value={50}>50 par page</option>
          </select>
        </div>

        <table className="societes-table">
  <thead>
    <tr>
      <th>Nom</th>
      <th>SIRET</th>
      <th>Demandes</th>
      <th>Adresse</th>
      <th>Location</th>
      <th>Contact principal</th>
      <th>Autres contacts</th>
      <th>Actions</th>
    </tr>
  </thead>
  <tbody>
    {currentCompanies.length === 0 ? (
      <tr><td colSpan={8} style={{ textAlign: "center" }}>Aucune société.</td></tr>
    ) : currentCompanies.map(({ companyKey, ...c }) => {
      const location = [c.postalCode, c.city, countries.find(x => x.value === c.country)?.label || c.country]
        .filter(Boolean).join(", ");
      const principalFullName = `${c.principalGender || "Mr"} ${c.principalContact || ""}${c.principalFirstName ? " " + c.principalFirstName : ""}`.trim();
      return (
        <tr key={c.id}>
          <td>{c.name}</td>
          <td>{c.siret}</td>
          <td>{formatDemandes(c)}</td>
          <td>{c.address}</td>
          <td>{location}</td>
          <td>
            {principalFullName ? (
              <button className="contact-button" onClick={() => setSelectedContact({
                name: principalFullName,
                phone: c.principalPhone ?? "",
                email: c.principalEmail ?? "",
                fonction: c.principalPosition ?? "",
                gender: c.principalGender,
                type: "principal",
                principalContact: c.principalContact,
                principalFirstName: c.principalFirstName,
              })}>
                {principalFullName}
              </button>
            ) : (<em>Aucun contact</em>)}
          </td>
          <td>
            {(c.otherContacts || []).length === 0 ? <em>Aucun</em> : (
              (c.otherContacts || []).length === 1 ? (
                (() => {
                  const ct = (c.otherContacts || [])[0];
                  const label = `${ct.civilite || "Mr"} ${ct.nom} ${ct.prenom || ""}`.trim();
                  return (
                    <button className="contact-button" onClick={() => setSelectedContact({
                        name: label,
                        phone: ct.tel,
                        email: ct.email,
                        fonction: ct.fonction,
                        gender: ct.civilite,
                        type: "autre",
                        nom: ct.nom,
                        prenom: ct.prenom,
                      })}>
                      {label}
                    </button>
                  );
                })()
              ) : (
                <ul style={{ margin: 0, paddingLeft: "1rem" }}>
                  {(c.otherContacts || []).map((ct, i) => {
                    const label = `${ct.civilite || "Mr"} ${ct.nom} ${ct.prenom || ""}`.trim();
                    return (
                      <li key={i}>
                        <button className="contact-button" onClick={() => setSelectedContact({
                          name: label,
                          phone: ct.tel,
                          email: ct.email,
                          fonction: ct.fonction,
                          gender: ct.civilite,
                          type: "autre",
                          nom: ct.nom,
                          prenom: ct.prenom,
                        })}>
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )
            )}
          </td>
          <td className="actions-column">
            <button className="edit-button" onClick={() => { setCompanyToEdit(c); setIsModalOpen(true); }} title="Modifier"><i className="bi bi-pencil-square"></i></button>
            <button className="delete-button" onClick={() => handleDeleteCompany(companyKey)} title="Supprimer"><i className="bi bi-trash"></i></button>
            <button 
              className="edit-button" 
              onClick={() => navigate('/opportunites-foodinnov', { state: { preselectedCompany: c.name } })} 
              title="Créer une demande pour cette société"
              style={{ marginLeft: '5px' }}
            >
              <i className="bi bi-arrow-right"></i>
            </button>
          </td>
        </tr>
      );
    })}
  </tbody>
</table>


        <div className="pagination">
          <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>
            Précédent
          </button>
          <span>Page {currentPage} / {totalPages}</span>
          <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
            Suivant
          </button>
        </div>
      </main>

      {isModalOpen && !selectedFromInsee && (
        <SocieteModal
          onClose={handleCloseModal}
          onSubmit={handleSocieteSubmit}
          initialData={companyToEdit || undefined}
        />
      )}

      {isInseeModalOpen && (
        <InseeCompanySearch
          apiKey={process.env.REACT_APP_INSEE_API_KEY || ""}
          onClose={() => {
            setIsInseeModalOpen(false);
            setSelectedFromInsee(null);
          }}
          onSelect={(partial) => {
            setSelectedFromInsee(partial);
            setIsInseeModalOpen(false);
            setIsModalOpen(true);
          }}
        />
      )}

      {selectedFromInsee && (
        <SocieteModal
          onClose={handleCloseModal}
          onSubmit={handleSocieteSubmit}
          initialData={selectedFromInsee as Company}
        />
      )}

      {selectedContact && (
        <div className="contact-popup-overlay">
          <div className="contact-popup-card">
            <button className="close-popup-btn" onClick={() => setSelectedContact(null)}>✕</button>
            <div className="contact-popup-content">
              <div className={`contact-avatar ${
                selectedContact.gender === "Mme"
                  ? "avatar-female"
                  : selectedContact.gender === "Mr"
                  ? "avatar-male"
                  : "avatar-neutral"
              }`}>
                {(selectedContact.type === "principal"
                  ? `${(selectedContact.principalContact || "").charAt(0)}${(selectedContact.principalFirstName || "").charAt(0)}`
                  : `${(selectedContact.nom || "").charAt(0)}${(selectedContact.prenom || "").charAt(0)}`
                ).toUpperCase()}
              </div>
              <h3>{selectedContact.name}</h3>
              {selectedContact.fonction && <p className="contact-role">{selectedContact.fonction}</p>}
              <hr />
              <div className="contact-info">
                <p><strong>Téléphone :</strong> {selectedContact.phone || "Non renseigné"}</p>
                <p><strong>Email :</strong> {selectedContact.email || "Non renseigné"}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {companyToDelete && (
        <div className="modal-overlay">
          <div className="delete-confirmation">
            <h2>Confirmer la suppression</h2>
            <p>Êtes-vous sûr de vouloir supprimer la société "{companyToDelete.name}" ?</p>
            <p className="warning-text">Cette action est irréversible.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setCompanyToDelete(null)}>
                Annuler
              </button>
              <button className="delete-button" onClick={confirmDelete}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
      <Footer />
    </div>
  );
};

export default SocietesFoodinnov;
